import React, { useState } from 'react';
import { usePageMeta } from '../hooks/usePageMeta';
import styles from './ContactPage.module.css';

const ContactPage = () => {
  usePageMeta({
    title: 'Contact',
    path: '/contact',
    description: 'Contact DarkNews — general inquiries. Copyright and feed-removal requests go to /copyright.',
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    // Simulate form submission (replace with actual API call)
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      setSubmitStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`more-page ${styles.contactPage}`}>
      <div className="container">
        <div className={`more-page-header ${styles.pageHeader}`}>
          <h1>Contact Us</h1>
          <p className={styles.subtitle}>Get in touch with the DarkNews team</p>
        </div>

        <div className={styles.content}>
          <div className={styles.contactGrid}>
            <div className={styles.contactInfo}>
              <section className={styles.section}>
                <h2>Get in Touch</h2>
                <p>
                  We'd love to hear from you! Whether you have questions about our platform, 
                  suggestions for improvement, or feedback on our content, we're here to help.
                </p>

                <div className={styles.contactMethods}>
                  <div className={styles.contactMethod}>
                    <h3>General Inquiries</h3>
                    <p><a href="mailto:info@darknews.ai">info@darknews.ai</a></p>
                    <p className={styles.contactNote}>For general questions and information</p>
                  </div>

                  <div className={styles.contactMethod}>
                    <h3>Technical Support</h3>
                    <p><a href="mailto:support@darknews.ai">support@darknews.ai</a></p>
                    <p className={styles.contactNote}>For technical issues and bug reports</p>
                  </div>

                  <div className={styles.contactMethod}>
                    <h3>Privacy & Legal</h3>
                    <p><a href="mailto:privacy@darknews.ai">privacy@darknews.ai</a></p>
                    <p className={styles.contactNote}>For privacy concerns and legal matters</p>
                  </div>

                  <div className={styles.contactMethod}>
                    <h3>Copyright / DMCA</h3>
                    <p>
                      <a href="/copyright">darknews.ai/copyright</a>
                    </p>
                    <p>
                      <a href="mailto:copyright@darknews.ai">copyright@darknews.ai</a>
                    </p>
                    <p className={styles.contactNote}>
                      Ask us to remove your RSS feed — form saves to our ops inbox, no webmail required
                    </p>
                  </div>

                  <div className={styles.contactMethod}>
                    <h3>Content & Sources</h3>
                    <p><a href="mailto:content@darknews.ai">content@darknews.ai</a></p>
                    <p className={styles.contactNote}>For content suggestions and source recommendations</p>
                  </div>
                </div>
              </section>

              <section className={styles.section}>
                <h2>Response Times</h2>
                <div className={styles.responseTimes}>
                  <div className={styles.responseItem}>
                    <strong>General Inquiries:</strong> 24-48 hours
                  </div>
                  <div className={styles.responseItem}>
                    <strong>Technical Support:</strong> 12-24 hours
                  </div>
                  <div className={styles.responseItem}>
                    <strong>Copyright / DMCA:</strong> 48 hours to acknowledge; 5 business days to act
                  </div>
                  <div className={styles.responseItem}>
                    <strong>Content & Sources:</strong> 24-48 hours
                  </div>
                </div>
              </section>

              <section className={styles.section}>
                <h2>Business Information</h2>
                <div className={styles.businessInfo}>
                  <p><strong>Website:</strong> <a href="https://darknews.ai">https://darknews.ai</a></p>
                  <p><strong>Platform:</strong> AI-powered news aggregation</p>
                  <p><strong>Founded:</strong> 2024</p>
                  <p><strong>Mission:</strong> Anti-clickbait, anti-partisan; truth over theater</p>
                </div>
              </section>
            </div>

            <div className={styles.contactForm}>
              <section className={styles.section}>
                <h2>Send us a Message</h2>
                <form onSubmit={handleSubmit} className={styles.form}>
                  <div className={styles.formGroup}>
                    <label htmlFor="name" className={styles.label}>
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className={styles.input}
                      required
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="email" className={styles.label}>
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={styles.input}
                      required
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="subject" className={styles.label}>
                      Subject *
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      className={styles.select}
                      required
                    >
                      <option value="">Select a subject</option>
                      <option value="general">General Inquiry</option>
                      <option value="technical">Technical Support</option>
                      <option value="copyright">Copyright / feed removal</option>
                      <option value="privacy">Privacy & Legal</option>
                      <option value="content">Content & Sources</option>
                      <option value="feedback">Feedback & Suggestions</option>
                      <option value="partnership">Partnership Opportunities</option>
                      <option value="other">Other</option>
                    </select>
                    {formData.subject === 'copyright' && (
                      <p className={styles.contactNote}>
                        For feed removal, use the{' '}
                        <a href="/copyright">Copyright / DMCA</a> form — this contact form is a
                        stub and does not reach our ops inbox.
                      </p>
                    )}
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="message" className={styles.label}>
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      className={styles.textarea}
                      rows="6"
                      placeholder="Please provide as much detail as possible..."
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    className={styles.submitButton}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>

                  {submitStatus === 'success' && (
                    <div className={styles.successMessage}>
                      ✅ Thank you! Your message has been sent successfully. We'll get back to you soon.
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className={styles.errorMessage}>
                      ❌ Sorry, there was an error sending your message. Please try again or contact us directly via email.
                    </div>
                  )}
                </form>
              </section>
            </div>
          </div>

          <section className={styles.section}>
            <h2>Frequently Asked Questions</h2>
            <div className={styles.faq}>
              <div className={styles.faqItem}>
                <h3>How do you select your news sources?</h3>
                <p>
                  We carefully curate our sources based on credibility, diversity of perspective, 
                  and quality of reporting. We include established sources like BBC and Reuters, 
                  as well as independent publications and specialized sources.
                </p>
              </div>

              <div className={styles.faqItem}>
                <h3>Is DarkNews free to use?</h3>
                <p>
                  Yes, our basic news aggregation service is completely free. We may introduce 
                  premium features in the future, but core functionality will remain accessible 
                  to all users.
                </p>
              </div>

              <div className={styles.faqItem}>
                <h3>How often is content updated?</h3>
                <p>
                  Our RSS harvesting system runs continuously, updating content throughout the day. 
                  New articles typically appear within minutes of being published by the original sources.
                </p>
              </div>

              <div className={styles.faqItem}>
                <h3>Can I suggest new sources for inclusion?</h3>
                <p>
                  Absolutely. Email{' '}
                  <a href="mailto:content@darknews.ai">content@darknews.ai</a> with your recommendations.
                </p>
              </div>

              <div className={styles.faqItem}>
                <h3>Can I ask you to stop harvesting my site?</h3>
                <p>
                  Yes. Use the <a href="/copyright">Copyright / DMCA</a> form with the site or feed
                  URL. We will disable matching RSS feeds. You can also write{' '}
                  <a href="mailto:copyright@darknews.ai">copyright@darknews.ai</a>.
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
